import axios from "axios";
import withConfig from "@growmark/axios/core";
import { bearer } from "@growmark/axios/config";
import { cognito } from "@growmark/axios/authn/cognito";
import instance from '@growmark/cognito-vue';
import { ENDPOINTS } from '@growmark/energy-llc-api/types';
import { TICKET_TAXES, TAX } from "@growmark/energy-llc-api/types/dist/src/common/types/tax";
import { TICKET_DETAILS } from "@growmark/energy-llc-api/types/dist/src/common/types/ticket";
import { INSTRUCTIONS } from "@growmark/energy-llc-api/types/dist/src/common/types/instructions";
import { TICKETS } from "@growmark/energy-llc-api/types/dist/src/member/types/ticket"
import { LOCATION, LOCATION_PRODUCT } from "@growmark/energy-llc-api/types/dist/src/common/types/location";
import { PRODUCTS } from "@growmark/energy-llc-api/types/dist/src/common/types/product";
import { CATEGORY } from "@growmark/energy-llc-api/types/dist/src/common/types/category";
import { PART } from "@growmark/energy-llc-api/types/dist/src/common/types/part";
import { signInWithRedirect } from 'aws-amplify/auth';

export { retry, keyed, lagging, leading, longrunning, abortable, shared, versioned } from '@growmark/axios/config';
const signin = async () => { await signInWithRedirect({ provider: { custom: 'GROWMARKHostedLogin' } }); };
export const authed = bearer(cognito(instance, { signin }), { header: 'Authorization' });
export type useAPIFunction = typeof withConfig extends (...params: never) => infer X ? X : never;

axios.defaults.headers.common["Accept"] = "application/json";
axios.defaults.timeout = 30 * 60 * 1000;

const getApi = () => withConfig<ENDPOINTS.MEMBER>([authed])(axios.create({
  baseURL: `${process.env.VUE_APP_SERVICE_API_URL}`,
  timeout: 30 * 60 * 1000
}))

export interface TicketQueryParams {
  offset?: string;
  pageSize?: string;
  startDate?: string;
  endDate?: string;
  ticketRef?: string;
  ticketProduct?: string;
  location?: string;
  status?: string;
  customer?: string;
}

export async function getTickets(params: TicketQueryParams): Promise<TICKETS[]> {
  const api = getApi();
  const response = await api.get({ url: '/tickets', query: params });
  return response.data as any[];
}
export async function getTicket(ticketRef: string): Promise<TICKETS[]> {
  const api = getApi();
  const response = await api.get({
    url: '/ticket/{ticket}/',
    path: { ticket: ticketRef }
  });
  return response.data as TICKETS[];
}
export async function postTicket(ticket: string): Promise<TICKETS> {
  if (!ticket) {
    throw new Error("Ticket reference is required to create a ticket.");
  }
  const api = getApi();
  const response = await api.post({
    url: '/ticket',
    data: { ticket: ticket }
  });
  return response.data
}

export interface PutTicketBody {
  client: string;
  type?: string;
  [key: string]: unknown;
}

export async function putTicket(ticketRef: string, body: PutTicketBody): Promise<{ id: number; message: string }> {
  const api = getApi();
  const response = await api.put({
    url: '/ticket/{ticket}',
    path: { ticket: ticketRef },
    data: body
  });
  return response.data as { id: number; message: string };
}
export async function putTicketStatus(ticketRef: string, body: any) {
  const api = getApi();
  const response = await api.put({
    url: '/ticket/{ticket}/status',
    path: { ticket: ticketRef },
    data: body
  });
  return response.data;
}

export async function getTicketsExcel(params: TicketQueryParams): Promise<{ data: any; headers: any }> {
  const api = getApi();
  const response = await api.get({
    url: '/tickets/excel',
    query: params
  });
  return { data: response.data, headers: response.headers };
}

export async function getTicketTaxes(ticket: string, client: string, account: string, location: string): Promise<TICKET_TAXES[]> {
  const api = getApi();
  const response = await api.get({
    url: '/ticket/{ticket}/taxes',
    path: { ticket: ticket },
    query: { client, account, location }
  });
  return response.data as any[];
}

export async function getTicketDetails(ticket: string): Promise<TICKET_DETAILS[]> {
  const api = getApi();
  const response = await api.get({
    url: '/ticket/{ticket}/details/',
    path: { ticket: ticket }
  });
  return response.data as any[];
}

export async function getTicketPDF(ticket: string, client: string, account: string, location: string): Promise<string> {
  const api = getApi();
  const response = await api.get({
    url: '/ticket/{ticket}/pdf/',
    path: { ticket: ticket },
    query: { client, account, location }
  });
  return response.data;
}

export async function getTaxGroupTaxes(taxgroup: string): Promise<TAX[]> {
  const api = getApi();
  const response = await api.get({
    url: `/taxgroup/{taxgroup}/taxes`,
    path: { taxgroup: taxgroup }
  });
  return response.data as TAX[];
}

export async function getLocations(customer?: string): Promise<LOCATION[]> {
  const api = getApi();
  const response = await api.get({
    url: "/locations",
    query: { customer: customer }
  });
  return response.data as LOCATION[];
}

export async function getAffiliation(): Promise<any> {
  const api = getApi();
  const response = await api.get({
    url: "/affiliation"
  });
  return response.data;
}

export async function getProducts(): Promise<PRODUCTS[]> {
  const api = getApi();
  const response = await api.get({
    url: "/products"
  });
  return response.data as PRODUCTS[];
}

export async function getLocationProducts(account: string, location: string): Promise<LOCATION_PRODUCT[]> {
  const api = getApi();
  const response = await api.get({
    url: '/location/product',
    query: { account: account, location: location }
  });
  return response.data as LOCATION_PRODUCT[];
}
export async function getInstructions(account: string, location: string): Promise<INSTRUCTIONS[]> {
  const api = getApi();
  const response = await api.get({
    url: '/instructions',
    query: { account: account, location: location }
  });
  return response.data as INSTRUCTIONS[];
}

export async function getCategories(): Promise<CATEGORY[]> {
  const api = getApi();
  const response = await api.get({
    url: '/categories'
  });
  return response.data as CATEGORY[];
}

export async function getParts(category: string): Promise<PART[]> {
  const api = getApi();
  const response = await api.get({
    url: '/parts',
    query: { category: category }
  });
  return response.data as PART[];
}

export async function postTicketLineItem(
  ticket: string,
  body: { client: string; ltype: string;[key: string]: unknown }
): Promise<{ seq: number; id: number; message: string }> {
  const api = getApi();
  const response = await (api as any).post({
    url: '/ticket/{ticket}/line-item',
    path: { ticket: ticket },
    data: body
  });
  return response.data as { seq: number; id: number; message: string };
}

export async function putTicketLineItem(
  ticket: string,
  seq: number,
  body: { client: string; ltype: string;[key: string]: unknown }
): Promise<{ id: number; message: string }> {
  const api = getApi();
  const response = await (api as any).put({
    url: '/ticket/{ticket}/line-item/{seq}',
    path: { ticket: ticket, seq: String(seq) },
    data: body
  });
  return response.data as { id: number; message: string };
}

export async function getVendorTaxing(client: string, account: string, location: string): Promise<any[]> {
  const api = getApi();
  const response = await api.get({
    url: '/vendor-taxing',
    query: { client, account, location }
  });
  return response.data as any[];
}
